import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { RingSizes } from 'content/products/ring/productMap/enums';

export enum RingStackType {
  SILVER_HALO = 'SILVER_HALO',
  GOLD_HALO = 'GOLD_HALO',
  NONE = 'NONE',
}

// Stores that cannot currently service bling rings (silver or gold).
// To disable bling for a store, add it to this set.
export const BLING_DISABLED_STORES = new Set<ShopifyStore>();

export const isBlingEnabledForStore = (store?: ShopifyStore | null): boolean =>
  !!store && !BLING_DISABLED_STORES.has(store);

// Percentage off every Bling SKU, by store — ROW carries the rest of the world.
// Display only: the charged price still comes from Shopify, so these must match
// the discounts configured there. Empty the map to turn the campaign off.

// AU, SA, ZA, JP, and MX check out through the same Shopify shop as EU/GB
// (see getBaseStore), which has one shared discount for that whole shop —
// verified  that all five are actually charged the EU/GB rate, not ROW's. 
// They're listed at 20% here to match what Shopify charges,
// not because the campaign was designed to give them the EU/GB rate.
export const BLING_DISCOUNT_PERCENTAGE: Partial<Record<ShopifyStore, number>> =
  {
    [ShopifyStore.IN]: 30,
    [ShopifyStore.AE]: 10,
    [ShopifyStore.EU]: 20,
    [ShopifyStore.GB]: 20,
    [ShopifyStore.AU]: 20,
    [ShopifyStore.SA]: 20,
    [ShopifyStore.ZA]: 20,
    [ShopifyStore.JP]: 20,
    [ShopifyStore.MX]: 20,
    [ShopifyStore.ROW]: 15,
  };

export const getBlingDiscountPercentage = (
  store?: ShopifyStore | null,
): number => {
  if (!isBlingEnabledForStore(store)) return 0;
  return (
    (store ? BLING_DISCOUNT_PERCENTAGE[store] : undefined) ??
    BLING_DISCOUNT_PERCENTAGE[ShopifyStore.ROW] ??
    0
  );
};

export const applyBlingDiscount = (
  price: number,
  store?: ShopifyStore | null,
): number => price * (1 - getBlingDiscountPercentage(store) / 100);

// Marketing-set saving on the Duo stack vs two singles. Understates the real
// spread (11.1% IN to 18.4% GB) — kept as-is so the copy does not change.
export const DUO_SAVE_PERCENTAGE = 10;

export const getBlingStrikedPrice = (
  price: number,
  store?: ShopifyStore | null,
): number | undefined =>
  getBlingDiscountPercentage(store) > 0 && price > 0 ? price : undefined;

// Stores where Gold Halo (single + duo) is marked sold-out. Silver Halo stays
// purchasable. To mark a store sold-out, add it to this set.
export const GOLD_HALO_SOLD_OUT_STORES = new Set<ShopifyStore>();

export const isGoldHaloSoldOut = (store?: ShopifyStore | null): boolean =>
  !!store && GOLD_HALO_SOLD_OUT_STORES.has(store);

// Sizes with no sellable SKU for a stack type, on every store. Empty to re-enable.
export const STACK_UNAVAILABLE_SIZES: Partial<
  Record<RingStackType, RingSizes[]>
> = {
  [RingStackType.GOLD_HALO]: [RingSizes.SIX],
};

export const isStackSizeUnavailable = (
  stackType: RingStackType,
  size?: RingSizes | string | number | null,
): boolean =>
  size != null &&
  (STACK_UNAVAILABLE_SIZES[stackType]?.includes(Number(size) as RingSizes) ??
    false);

export enum StackVariant {
  SINGLE = 'SINGLE',
  DUO = 'DUO',
}

export type StackOptionConfig = {
  id: RingStackType;
  titleKey: string;
  descriptionKey: string;
  thumbnail: string;
  isRecommended: boolean;
};

export type StackSubVariantConfig = {
  id: StackVariant;
  titleKey: string;
  descriptionKey: string;
  isRecommended: boolean;
  showPriceInTitle: boolean;
  thumbnails: Record<
    RingStackType.SILVER_HALO | RingStackType.GOLD_HALO,
    string
  >;
};

export const STACK_OPTIONS: StackOptionConfig[] = [
  {
    id: RingStackType.SILVER_HALO,
    titleKey: 'ring.productSelection.stacking.silverHalo.title',
    descriptionKey: 'ring.productSelection.stacking.silverHalo.description',
    thumbnail: 'web_v2/ring-buy/color-cta/ss-3.png',
    isRecommended: true,
  },
  {
    id: RingStackType.GOLD_HALO,
    titleKey: 'ring.productSelection.stacking.goldHalo.title',
    descriptionKey: 'ring.productSelection.stacking.goldHalo.description',
    thumbnail: 'web_v2/ring-buy/color-cta/bg-3.png',
    isRecommended: false,
  },
];

// 41 approved countries for bling/stacking ring products (40 from GnJ list + IN)
export const ALLOWED_BLING_COUNTRIES = new Set([
  'AE',
  'AT',
  'AU',
  'BE',
  'CA',
  'CH',
  'CN',
  'CZ',
  'DE',
  'DK',
  'ES',
  'FI',
  'FJ',
  'FR',
  'GB',
  'GR',
  'HK',
  'ID',
  'IE',
  'IN',
  'IT',
  'JP',
  'KE',
  'KR',
  'KW',
  'LU',
  'NG',
  'NL',
  'NO',
  'NZ',
  'PA',
  'PL',
  'RO',
  'SA',
  'SE',
  'SG',
  'SI',
  'TH',
  'US',
  'XM',
  'ZA',
]);

export const isBlingAvailable = (countryCode: string): boolean =>
  ALLOWED_BLING_COUNTRIES.has(countryCode.toUpperCase());

// Combines both gates — country approval and the store kill switch — so a
// call site can't show Bling while forgetting one of the two.
export const isBlingVisible = (
  countryCode: string,
  store?: ShopifyStore | null,
): boolean => isBlingAvailable(countryCode) && isBlingEnabledForStore(store);

export const STACK_SUB_VARIANTS: StackSubVariantConfig[] = [
  {
    id: StackVariant.SINGLE,
    titleKey: 'ring.productSelection.stacking.singleRing.title',
    descriptionKey: 'ring.productSelection.stacking.singleRing.description',
    isRecommended: false,
    showPriceInTitle: false,
    thumbnails: {
      [RingStackType.SILVER_HALO]:
        'web_v2/ring-buy/stacking_options/silver_single.png',
      [RingStackType.GOLD_HALO]:
        'web_v2/ring-buy/stacking_options/gold_single.png',
    },
  },
  {
    id: StackVariant.DUO,
    titleKey: 'ring.productSelection.stacking.duoRings.title',
    descriptionKey: 'ring.productSelection.stacking.duoRings.description',
    isRecommended: true,
    showPriceInTitle: true,
    thumbnails: {
      [RingStackType.SILVER_HALO]:
        'web_v2/ring-buy/stacking_options/silver_duo.png',
      [RingStackType.GOLD_HALO]:
        'web_v2/ring-buy/stacking_options/gold_duo.png',
    },
  },
];
